import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '../styles/ThemeProvider';
import { TargetSpecType, LLMSettings, LLMJudgeMetrics, DEFAULT_LLM_SETTINGS } from '../types';
import { Theme } from '../styles/theme';
import { LLMSettingsPanel } from './LLMSettingsPanel';
import { LLMJudgeRibbon } from './LLMJudgeRibbon';
import { ButtonRow, GuardrailAlertBlock } from './GuardrailAlertBlock';
import { getFileExtension, HPS_FILE_TYPES } from '../constants/hpsFileTypes';

const UploadContainer = styled.div<{ colors: Theme['colors'] }>`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h2<{ color: string }>`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.color};
`;

const Select = styled.select<{ borderColor: string; focusColor: string }>`
  padding: 12px 16px;
  border: 2px solid ${props => props.borderColor};
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${props => props.focusColor};
    box-shadow: 0 0 0 3px ${props => props.focusColor}20;
  }
`;

const AiBadgeRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
`;

const AiBadge = styled.div<{ bgColor: string; textColor: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  background: ${props => props.bgColor};
  color: ${props => props.textColor};
  font-size: 14px;
  font-weight: 600;
`;

const AiHint = styled.p<{ color: string }>`
  font-size: 12px;
  color: ${props => props.color};
  margin-top: 2px;
`;

const DropZone = styled.div<{ isDragging: boolean; accentColor: string; borderColor: string }>`
  border: 2px dashed ${props => (props.isDragging ? props.accentColor : props.borderColor)};
  border-radius: 12px;
  padding: 48px 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => (props.isDragging ? `${props.accentColor}10` : 'transparent')};

  &:hover {
    border-color: ${props => props.accentColor};
    background: ${props => props.accentColor}05;
  }
`;

const UploadIcon = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: ${props => props.color}15;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UploadText = styled.p<{ color: string }>`
  font-size: 16px;
  color: ${props => props.color};
  margin-bottom: 8px;
`;

const UploadHint = styled.p<{ color: string }>`
  font-size: 14px;
  color: ${props => props.color};
`;

const SelectedFile = styled.div<{ bgColor: string; textColor: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.bgColor};
  border-radius: 8px;
  margin-top: 16px;
`;

const FileName = styled.span<{ color: string }>`
  font-weight: 500;
  color: ${props => props.color};
`;

const FileSize = styled.span<{ color: string }>`
  font-size: 14px;
  color: ${props => props.color};
`;

const MapButton = styled.button<{ bgColor: string; hoverColor: string }>`
  width: 100%;
  padding: 16px 32px;
  background: linear-gradient(135deg, ${props => props.bgColor} 0%, ${props => props.hoverColor} 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px ${props => props.bgColor}40;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const UploadErrorModal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.5);
`;

const UploadErrorCard = styled.div`
  width: min(480px, 100%);
  padding: 28px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.25);
`;

const UploadErrorTitle = styled.h3`
  margin: 0 0 12px;
  color: #a8071a;
`;

const UploadErrorText = styled.p`
  margin: 0 0 20px;
  line-height: 1.5;
`;



interface SourceFileUploadProps {
  targetSpec: TargetSpecType;
  onTargetSpecChange: (spec: TargetSpecType) => void;
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  onMap: () => void;
  isMapping: boolean;
  llmSettings?: LLMSettings;
  onSettingsChange?: (settings: LLMSettings) => void;
  judgeMetrics?: LLMJudgeMetrics | null;
  judgeLoading?: boolean;
  guardrailError?: { category: string; message: string } | null;
}

export const SourceFileUpload: React.FC<SourceFileUploadProps> = ({
  targetSpec,
  onTargetSpecChange,
  onFileSelect,
  selectedFile,
  onMap,
  isMapping,
  llmSettings = DEFAULT_LLM_SETTINGS,
  onSettingsChange,
  judgeMetrics,
  judgeLoading,
  guardrailError,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const expectedFileType = HPS_FILE_TYPES[targetSpec];

  const selectFile = (file: File) => {
    const extension = getFileExtension(file.name);
    if (extension !== expectedFileType.extension) {
      setUploadError(`Only ${expectedFileType.extension} files can be uploaded for ${expectedFileType.label}. Please select a file with the required extension.`);
      return;
    }
    onFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) selectFile(file);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) selectFile(file);
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <UploadContainer colors={theme.colors}>
      <SectionTitle color={theme.colors.optumBlue}>
        {/* Mapping icon */}
        <svg width="24" height="24" viewBox="0 0 24 24" fill={theme.colors.optumOrange}>
          <path d="M16 4h2a2 2 0 012 2v1m0 4v2m0 4v1a2 2 0 01-2 2h-2M8 20H6a2 2 0 01-2-2v-1m0-4v-2m0-4V6a2 2 0 012-2h2" stroke={theme.colors.optumOrange} strokeWidth="2" fill="none" strokeLinecap="round"/>
          <circle cx="8" cy="8" r="2" fill={theme.colors.optumOrange}/>
          <circle cx="16" cy="12" r="2" fill={theme.colors.optumBlue}/>
          <circle cx="8" cy="16" r="2" fill={theme.colors.optumOrange}/>
          <line x1="10" y1="8" x2="14" y2="12" stroke={theme.colors.accentTeal} strokeWidth="1.5" strokeDasharray="3 2"/>
          <line x1="10" y1="16" x2="14" y2="12" stroke={theme.colors.accentTeal} strokeWidth="1.5" strokeDasharray="3 2"/>
        </svg>
        Upload Source Data File
      </SectionTitle>

      <FormRow>
        <FormGroup>
          <Label color={theme.colors.textPrimary}>Target HPS Specification</Label>
          <Select
            value={targetSpec}
            onChange={e => onTargetSpecChange(e.target.value as TargetSpecType)}
            borderColor={theme.colors.border}
            focusColor={theme.colors.optumBlue}
          >
            <option value="claimstatus30">Claim Status 3.0</option>
            <option value="eligibility31">Eligibility 3.1</option>
            <option value="eligibility30">Eligibility 3.0</option>
            <option value="individualusagebenefit30">Individual Usage Benefit 3.0</option>
            <option value="planbenefit30">Plan Benefit 3.0</option>
            <option value="standardbenefitentity30">Standard Benefit Entity 3.0</option>
          </Select>
        </FormGroup>
        <FormGroup>
          <Label color={theme.colors.textPrimary}>AI Engine</Label>
          <AiBadgeRow>
            <AiBadge bgColor={`${theme.colors.success}15`} textColor={theme.colors.success}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              Optum AI Gateway — GPT-5
            </AiBadge>
          </AiBadgeRow>
          <AiHint color={theme.colors.textMuted}>
            Powered by UHG internal LLM via HCP App Identity. Automatic — no keys needed.
          </AiHint>
        </FormGroup>
      </FormRow>

      <DropZone
        isDragging={isDragging}
        accentColor={theme.colors.accentTeal}
        borderColor={theme.colors.border}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} accept={expectedFileType.extension} />
        <UploadIcon color={theme.colors.accentTeal}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accentTeal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </UploadIcon>
        <UploadText color={theme.colors.textPrimary}>
          Drop your {expectedFileType.extension} file here, or <span style={{ color: theme.colors.accentTeal, fontWeight: 600 }}>browse</span>
        </UploadText>
        <UploadHint color={theme.colors.textMuted}>Only {expectedFileType.extension} files are accepted for the selected {expectedFileType.label} specification.</UploadHint>
      </DropZone>

      {selectedFile && (
        <SelectedFile bgColor={`${theme.colors.success}15`} textColor={theme.colors.textPrimary}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill={theme.colors.success}>
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
            <polyline points="14 2 14 8 20 8" fill="none" stroke="white" strokeWidth="2" />
          </svg>
          <FileName color={theme.colors.textPrimary}>{selectedFile.name}</FileName>
          <FileSize color={theme.colors.textMuted}>({formatFileSize(selectedFile.size)})</FileSize>
        </SelectedFile>
      )}

      <ButtonRow>
        <MapButton
          onClick={onMap}
          disabled={!selectedFile || isMapping}
          bgColor={theme.colors.accentTeal}
          hoverColor={theme.colors.optumBlue}
        >
          {isMapping ? (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="spin">
                <path d="M21 12a9 9 0 11-6.219-8.56" />
              </svg>
              Analyzing &amp; Mapping...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              Generate Field Mapping
            </>
          )}
        </MapButton>
        {onSettingsChange && (
          <LLMSettingsPanel settings={llmSettings} onChange={onSettingsChange} />
        )}
      </ButtonRow>

      {guardrailError && (
        <GuardrailAlertBlock error={guardrailError} />
      )}

      <LLMJudgeRibbon
        metrics={judgeMetrics ?? null}
        loading={judgeLoading}
        label="Mapping Quality"
      />

      {uploadError && (
        <UploadErrorModal role="alertdialog" aria-modal="true" aria-labelledby="mapping-upload-error-title">
          <UploadErrorCard>
            <UploadErrorTitle id="mapping-upload-error-title">Unsupported file format</UploadErrorTitle>
            <UploadErrorText>{uploadError}</UploadErrorText>
            <ButtonRow>
              <MapButton type="button" bgColor={theme.colors.optumBlue} hoverColor={theme.colors.accentBlue} onClick={() => setUploadError(null)}>OK</MapButton>
            </ButtonRow>
          </UploadErrorCard>
        </UploadErrorModal>
      )}
    </UploadContainer>
  );
};
